import styled from "@emotion/styled";
import { GridActionsCellItem, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid-pro";
import { MouseEvent, SyntheticEvent } from "react";
import { TASK_ATTRIBUTE_DIALOG_TYPE } from "../../constants";
import DeleteIcon from "../../resources/images/icons-delete-blue.svg";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import { KLAYO_COLORS } from "../../themes";
import type { BaseTableRow, TableColumnDef, TableProps } from "../../types/components/table.types";
import { getHeaderName } from "../../types/components/table.types";
import { highlightKeyword } from "../../utilities";
import { Table } from "./Table";

/** Task row data structure */
interface TaskTableRow extends BaseTableRow {
  name?: string;
  numberOfCompetencies?: number;
  numberOfRegulations?: number;
  numberOfJobDefinitions?: number;
  deleted?: boolean;
  isTask?: boolean;
}

/** Props specific to TasksTable */
interface TasksTableProps extends TableProps<TaskTableRow> {
  onTaskAction?: (event: SyntheticEvent, row: TaskTableRow) => void;
  onPositionAction?: (event: SyntheticEvent, row: TaskTableRow) => void;
  onOpenTaskAttributesDialog?: (
    params: GridRenderCellParams<TaskTableRow>,
    type: (typeof TASK_ATTRIBUTE_DIALOG_TYPE)[keyof typeof TASK_ATTRIBUTE_DIALOG_TYPE]
  ) => void;
  onRowClick?: (row: GridRowParams<TaskTableRow>) => void;
  isUsingDeleteIcon?: boolean;
  keyword?: string;
  showProficiency?: boolean;
  rowHasAction?: boolean | null;
}

/** Styled component props */
interface KlayoTagProps {
  isTag: boolean;
}

export class TasksTable extends Table<TaskTableRow> {
  static columns = {
    name: {
      field: "name",
      label: "tableColumns.task"
    },
    competencies: {
      field: "numberOfCompetencies",
      label: "tableColumns.competencies"
    },
    regulations: {
      field: "numberOfRegulations",
      label: "tableColumns.regulations"
    },
    jobs: {
      field: "numberOfJobDefinitions",
      label: "tableColumns.jobs"
    },
    actions: {
      field: "actions",
      label: "tableColumns.actions"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [
      { type: TasksTable.columns.name },
      { type: TasksTable.columns.competencies },
      { type: TasksTable.columns.regulations },
      { type: TasksTable.columns.jobs },
      { type: TasksTable.columns.actions }
    ],
    noRowsMessage: "No rows, yet",
    showProficiency: false,
    rowHasAction: null
  };

  declare props: TasksTableProps;

  constructor(props: TasksTableProps) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: this.props.sortModel
        ? this.props.sortModel
        : [
            {
              field: "name",
              sort: "asc"
            }
          ]
    };
  }

  handleOnOpenTaskAttributesDialog = (
    value: GridRenderCellParams<TaskTableRow>,
    type: (typeof TASK_ATTRIBUTE_DIALOG_TYPE)[keyof typeof TASK_ATTRIBUTE_DIALOG_TYPE]
  ): void => {
    const { onOpenTaskAttributesDialog } = this.props;
    onOpenTaskAttributesDialog?.(value, type);
  };

  initColumns(): void {
    const { columns, sortable, onTaskAction, onPositionAction, isUsingDeleteIcon, keyword } =
      this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === TasksTable.columns.name) {
          this.columns.push({
            field: "name",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => highlightKeyword(params.row.name || "", keyword)
          });
        }

        if (c.type === TasksTable.columns.competencies) {
          this.columns.push({
            field: "numberOfCompetencies",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: "right",
            align: "right",
            valueGetter: (_value, row) => row.numberOfCompetencies,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params) => (
              <KlayoTag
                isTag={params.row.numberOfCompetencies !== 0}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  params.row.numberOfCompetencies !== 0 &&
                    this.handleOnOpenTaskAttributesDialog(
                      params,
                      TASK_ATTRIBUTE_DIALOG_TYPE.COMPETENCIES
                    );
                }}
              >
                {params.row.numberOfCompetencies}
              </KlayoTag>
            )
          });
        }

        if (c.type === TasksTable.columns.regulations) {
          this.columns.push({
            field: "numberOfRegulations",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: "right",
            align: "right",
            valueGetter: (_value, row) => row.numberOfRegulations,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params) => (
              <KlayoTag
                isTag={params.row.numberOfRegulations !== 0}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  params.row.numberOfRegulations !== 0 &&
                    this.handleOnOpenTaskAttributesDialog(
                      params,
                      TASK_ATTRIBUTE_DIALOG_TYPE.REGULATIONS
                    );
                }}
              >
                {params.row.numberOfRegulations}
              </KlayoTag>
            )
          });
        }

        if (c.type === TasksTable.columns.jobs) {
          this.columns.push({
            field: "numberOfJobDefinitions",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: "right",
            align: "right",
            valueGetter: (_value, row) => row.numberOfJobDefinitions,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params) => (
              <KlayoTag
                isTag={params.row.numberOfJobDefinitions !== 0}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  params.row.numberOfJobDefinitions !== 0 &&
                    this.handleOnOpenTaskAttributesDialog(params, TASK_ATTRIBUTE_DIALOG_TYPE.JOBS);
                }}
              >
                {params.row.numberOfJobDefinitions}
              </KlayoTag>
            )
          });
        }

        if (c.type === TasksTable.columns.actions) {
          this.columns.push({
            field: "actions",
            type: "actions",
            filterable: false,
            disableReorder: true,
            align: "right",
            valueGetter: (_value, row) => (row.deleted ? null : row),
            getActions: (params) =>
              params.row.deleted
                ? []
                : [
                    <GridActionsCellItem
                      icon={isUsingDeleteIcon ? <DeleteIcon /> : <MoreVertIcon />}
                      label='Actions'
                      onClick={(e) =>
                        params.row.isTask
                          ? onTaskAction?.(e, params.row)
                          : onPositionAction?.(e, params.row)
                      }
                      key={Math.random().toString(36).substring(7)}
                    />
                  ]
          });
        }
      }
    });
  }

  onRowClick = (row: GridRowParams<TaskTableRow>): void => {
    const { onRowClick } = this.props;
    onRowClick?.(row);
  };
}

const KlayoTag = styled.div<KlayoTagProps>`
  padding: 2px 12px;
  background-color: ${(props) =>
    props.isTag ? KLAYO_COLORS.NeonBlueNB050 : KLAYO_COLORS.transparent};
  border-radius: 100px;

  color: ${(props) => (props.isTag ? KLAYO_COLORS.NeonBlueNB500 : KLAYO_COLORS.GreyGR900)};
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  :hover {
    filter: ${(props) => (props.isTag ? "brightness(95%)" : "")};
    cursor: ${(props) => (props.isTag ? "pointer" : "")};
  }

  :focus {
    background-color: ${(props) => (props.isTag ? "#c6c7fc" : "")};
  }
`;
